export interface CreditActionType {
  action_code: string;
  action_name: string;
  description: string;
  category: string;
  default_credit_cost: number | null;
  status: string;
  is_active?: boolean | null;
}

export interface TierCreditCost {
  tier_id: string;
  tier_name: string;
  action_code: string;
  action_name: string;
  credit_cost: number | null;
}

export interface TierOption {
  id: string;
  name: string;
}

export interface PlanCreditCostRow {
  action_code: string;
  action_name: string;
  credit_cost: number | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getFirstValue = (
  record: Record<string, unknown>,
  keys: string[]
): unknown => {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) {
      return record[key];
    }
  }

  return undefined;
};

const toStringValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value);
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const findList = (data: unknown, keys: string[]): unknown[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (!isRecord(data)) {
    return [];
  }

  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
};

const recordValues = (record: Record<string, unknown>): unknown[] =>
  Object.keys(record).map((key) => record[key]);

const recordEntries = (record: Record<string, unknown>): Array<[string, unknown]> =>
  Object.keys(record).map((key) => [key, record[key]]);

const normalizeAction = (raw: unknown): CreditActionType | null => {
  if (!isRecord(raw)) {
    return null;
  }

  const actionCode = toStringValue(
    getFirstValue(raw, ['action_code', 'actionCode', 'code', 'Action Code'])
  ).trim();
  const actionName = toStringValue(
    getFirstValue(raw, ['action_name', 'actionName', 'action', 'name', 'Action'])
  ).trim();

  if (!actionCode && !actionName) {
    return null;
  }

  const isActiveValue = getFirstValue(raw, ['is_active', 'active']);
  const statusValue = getFirstValue(raw, ['status', 'Status']);
  const status =
    statusValue !== undefined && statusValue !== null
      ? toStringValue(statusValue)
      : typeof isActiveValue === 'boolean'
        ? isActiveValue
          ? 'Active'
          : 'Inactive'
        : '';

  return {
    action_code: actionCode,
    action_name: actionName || actionCode,
    description: toStringValue(getFirstValue(raw, ['description', 'Description'])),
    category: toStringValue(getFirstValue(raw, ['category', 'Category'])),
    default_credit_cost: toNumberOrNull(
      getFirstValue(raw, [
        'default_credit_cost',
        'defaultCreditCost',
        'default_cost',
        'credit_cost',
      ])
    ),
    status,
    is_active:
      typeof isActiveValue === 'boolean' ? isActiveValue : undefined,
  };
};

export const normalizeCreditActionsResponse = (
  data: unknown
): CreditActionType[] => {
  const list = findList(data, [
    'actions',
    'credit_actions',
    'credit_action_types',
    'items',
    'data',
    'results',
  ]);

  const rawActions = list.length > 0 ? list : isRecord(data) ? recordValues(data) : [];

  return rawActions
    .map(normalizeAction)
    .filter((action): action is CreditActionType => Boolean(action));
};

export const normalizeActionCodeOptionsResponse = (data: unknown): string[] => {
  const list = findList(data, ['codes', 'action_codes', 'items', 'data', 'results']);
  const rawCodes = list.length > 0 ? list : Array.isArray(data) ? data : [];
  const codes = rawCodes
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (isRecord(item)) {
        return toStringValue(
          getFirstValue(item, ['action_code', 'actionCode', 'code', 'value', 'name'])
        );
      }

      return '';
    })
    .map((code) => code.trim())
    .filter(Boolean);

  return Array.from(new Set(codes));
};

const expandActionCostMap = (
  raw: Record<string, unknown>,
  actionCosts: Record<string, unknown>
): TierCreditCost[] => {
  const tierId = toStringValue(getFirstValue(raw, ['tier_id', 'tierId', 'id'])).trim();
  const tierName = toStringValue(
    getFirstValue(raw, ['tier_name', 'tierName', 'name', 'Tier name'])
  ).trim();

  return recordEntries(actionCosts).map(([actionCode, cost]) => ({
    tier_id: tierId,
    tier_name: tierName,
    action_code: actionCode,
    action_name: actionCode,
    credit_cost: toNumberOrNull(cost),
  }));
};

const normalizeTierCost = (raw: unknown): TierCreditCost[] => {
  if (!isRecord(raw)) {
    return [];
  }

  const actionCosts = getFirstValue(raw, [
    'action_costs',
    'actionCosts',
    'credit_costs',
    'creditCosts',
  ]);

  if (isRecord(actionCosts)) {
    return expandActionCostMap(raw, actionCosts);
  }

  const nestedActions = findList(raw, ['actions', 'credit_actions', 'items']);
  if (nestedActions.length > 0) {
    const tierId = toStringValue(getFirstValue(raw, ['tier_id', 'tierId', 'id'])).trim();
    const tierName = toStringValue(
      getFirstValue(raw, ['tier_name', 'tierName', 'name'])
    ).trim();

    return nestedActions.reduce<TierCreditCost[]>((rows, nested) => {
      if (!isRecord(nested)) {
        return rows;
      }

      rows.push({
        tier_id: tierId,
        tier_name: tierName,
        action_code: toStringValue(
          getFirstValue(nested, ['action_code', 'actionCode', 'code'])
        ).trim(),
        action_name: toStringValue(
          getFirstValue(nested, ['action_name', 'actionName', 'action', 'name'])
        ).trim(),
        credit_cost: toNumberOrNull(
          getFirstValue(nested, ['credit_cost', 'creditCost', 'cost'])
        ),
      });

      return rows;
    }, []);
  }

  const actionCode = toStringValue(
    getFirstValue(raw, ['action_code', 'actionCode', 'code'])
  ).trim();

  if (!actionCode) {
    return [];
  }

  return [
    {
      tier_id: toStringValue(getFirstValue(raw, ['tier_id', 'tierId', 'id'])).trim(),
      tier_name: toStringValue(
        getFirstValue(raw, ['tier_name', 'tierName', 'name', 'Tier name'])
      ).trim(),
      action_code: actionCode,
      action_name: toStringValue(
        getFirstValue(raw, ['action_name', 'actionName', 'action', 'name'])
      ).trim(),
      credit_cost: toNumberOrNull(
        getFirstValue(raw, ['credit_cost', 'creditCost', 'cost'])
      ),
    },
  ];
};

export const normalizeTierCreditCostsResponse = (
  data: unknown
): TierCreditCost[] => {
  const list = findList(data, [
    'tiers',
    'tier_costs',
    'tier_credit_costs',
    'credit_actions',
    'actions',
    'items',
    'data',
    'results',
  ]);

  if (list.length > 0) {
    return list
      .reduce<TierCreditCost[]>((rows, item) => rows.concat(normalizeTierCost(item)), [])
      .filter((row) => Boolean(row.action_code));
  }

  if (isRecord(data)) {
    const tierGroups = getFirstValue(data, ['tiers', 'tier_costs', 'tier_credit_costs']);
    if (isRecord(tierGroups)) {
      return Object.values(tierGroups)
        .reduce<TierCreditCost[]>((rows, value) => {
          if (Array.isArray(value)) {
            return rows.concat(
              value.reduce<TierCreditCost[]>((innerRows, item) => innerRows.concat(normalizeTierCost(item)), [])
            );
          }

          if (isRecord(value)) {
            return rows.concat(normalizeTierCost(value));
          }

          return rows;
        }, [])
        .filter((row) => Boolean(row.action_code));
    }

    const topLevelActionCosts = getFirstValue(data, [
      'action_costs',
      'actionCosts',
      'credit_costs',
      'creditCosts',
    ]);

    if (isRecord(topLevelActionCosts)) {
      return expandActionCostMap(data, topLevelActionCosts);
    }

    return recordEntries(data).reduce<TierCreditCost[]>((rows, [tierId, value]) => {
      if (isRecord(value)) {
        const normalizedRows = normalizeTierCost({ id: tierId, ...value });
        if (normalizedRows.length > 0) {
          return rows.concat(normalizedRows);
        }

        return rows.concat(expandActionCostMap({ id: tierId }, value));
      }

      return rows;
    }, []);
  }

  return [];
};

export const deriveTierOptions = (
  planTiers: TierOption[],
  tierCosts: TierCreditCost[]
): TierOption[] => {
  const options = new Map<string, TierOption>();

  planTiers.forEach((tier) => {
    if (tier.id) {
      options.set(tier.id, tier);
    }
  });

  tierCosts.forEach((row) => {
    if (row.tier_id && !options.has(row.tier_id)) {
      options.set(row.tier_id, {
        id: row.tier_id,
        name: row.tier_name || row.tier_id,
      });
    }
  });

  return Array.from(options.values());
};

export const buildPlanCreditCostRows = (
  actions: CreditActionType[],
  tierCosts: TierCreditCost[],
  selectedTierId: string
): PlanCreditCostRow[] => {
  const selectedCosts = new Map<string, TierCreditCost>();

  tierCosts.forEach((row) => {
    if (row.tier_id === selectedTierId) {
      selectedCosts.set(row.action_code, row);
    }
  });

  return actions.map((action) => {
    const cost = selectedCosts.get(action.action_code);

    return {
      action_code: action.action_code,
      action_name: action.action_name,
      credit_cost: cost ? cost.credit_cost : null,
    };
  });
};

export const formatCreditCost = (cost: number | null | undefined): string => {
  if (cost === null || cost === undefined) {
    return '-';
  }

  return String(cost);
};

export const formatActionStatus = (action: CreditActionType): string => {
  if (action.status) {
    return action.status;
  }

  if (action.is_active === true) {
    return 'Active';
  }

  if (action.is_active === false) {
    return 'Inactive';
  }

  return '-';
};
